import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CellText,
  Modal,
  ProgressBar,
  TextInput,
} from '@opendate-app/setlist';

import DealNotesTimeline from '../../DealsDetail/components/DealNotesTimeline';
import { deriveNoteTitle } from '../../shared/components/CrmNotesCard';
import { formatMoney } from '../../shared/utils/crmMoney';
import { formatCloseDate } from '../../Deals/components/dealStages';
import { contactLabel, organizationLabel } from '../grantLabels';
import { REFRESH_ERROR } from '../refreshError';
import { nextReminderOn, recipientNames } from '../../Grants/grantReminders';
import {
  DOCUMENT_CATEGORY_LABELS,
  daysUntil,
  grantStageConfig,
  relativeDays,
  toAmount,
} from '../../Grants/grantStages';

const DASH = '—';
const PREVIEW_DOCUMENTS = 3;

const columns = { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 'var(--setlist-spacing-xl)' };
const mainColumn = { flex: '2 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-xl)' };
const sideColumn = { flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-xl)' };
const fieldGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 'var(--setlist-spacing-xl)' };
const rows = { display: 'flex', flexDirection: 'column', gap: 'var(--setlist-spacing-lg)' };
const description = { margin: 0, whiteSpace: 'pre-wrap', color: 'var(--sl-text-secondary)' };
const muted = { margin: 0, color: 'var(--sl-text-tertiary)', fontSize: 'var(--setlist-text-sm-size)' };

const money = (value) => formatMoney(toAmount(value)) || DASH;
const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';
const orDash = (value) => (isBlank(value) ? DASH : value);

const plainBody = (note) => (note.sanitized_body || note.body || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const fundingPercent = (grant) => {
  const requested = toAmount(grant.requested_amount);
  const awarded = toAmount(grant.awarded_amount);
  if (requested === null || awarded === null || requested <= 0) return null;
  return Math.min((awarded / requested) * 100, 100);
};

function Field({ label, children }) {
  return (
    <div data-field={label}>
      <CellText primary={children} supporting={label} />
    </div>
  );
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function SideCard({ title, actions, children }) {
  return (
    <Card data-testid={`overview-card-${title}`} header={<CardHeader title={title} actions={actions} />}>
      {children}
    </Card>
  );
}

SideCard.propTypes = {
  title: PropTypes.string.isRequired,
  actions: PropTypes.node,
  children: PropTypes.node,
};

function DetailsCard({ grant }) {
  const stage = grantStageConfig(grant.stage);
  return (
    <SideCard title="Details">
      <div style={fieldGrid}>
        <Field label="Grant name">{orDash(grant.name)}</Field>
        <Field label="Funder">{orDash(grant.funder)}</Field>
        <Field label="Status"><Badge color={stage.color} dot>{stage.label}</Badge></Field>
        <Field label="Requested amount">{money(grant.requested_amount)}</Field>
        <Field label="Awarded amount">{money(grant.awarded_amount)}</Field>
        <Field label="Grant owner">{orDash(grant.owner_name)}</Field>
        <Field label="Organization">{orDash(organizationLabel(grant))}</Field>
        <Field label="Primary contact">{orDash(contactLabel(grant))}</Field>
        <Field label="Application deadline">{formatCloseDate(grant.application_deadline_on)}</Field>
        <Field label="Submission date">{formatCloseDate(grant.submitted_on)}</Field>
        <Field label="Award decision date">{formatCloseDate(grant.decision_on)}</Field>
      </div>
    </SideCard>
  );
}

DetailsCard.propTypes = { grant: PropTypes.object.isRequired };

function reminderLine(grant, owners) {
  if (grant.reminders_enabled === false) return 'Reminders are off for this grant.';
  const nextOn = nextReminderOn(grant);
  if (!nextOn) return 'No more reminders scheduled.';
  const names = recipientNames(grant, owners);
  const line = `Next reminder ${formatCloseDate(nextOn)}`;
  return names.length ? `${line} to ${names.join(', ')}` : line;
}

function NextDeadlineCard({ grant, owners }) {
  const nextDeadline = grant.next_deadline;
  return (
    <SideCard title="Next deadline">
      <div style={rows}>
        <CellText
          primary={nextDeadline.label}
          supporting={`${relativeDays(daysUntil(nextDeadline.due_on))} · ${formatCloseDate(nextDeadline.due_on)}`}
        />
        <p style={muted}>{reminderLine(grant, owners)}</p>
      </div>
    </SideCard>
  );
}

NextDeadlineCard.propTypes = { grant: PropTypes.object.isRequired, owners: PropTypes.array.isRequired };

function OrganizationCard({ team, organization, contact, contactName }) {
  const named = !organization.archived && !isBlank(organization.name);
  const place = [organization.city, organization.state].filter((part) => !isBlank(part)).join(', ');
  const meta = [organization.org_type, place].filter((part) => !isBlank(part)).join(' · ');
  const email = (contact && contact.email) || organization.email;
  const awardedTotal = formatMoney(toAmount(organization.grants_awarded_total)) || '$0';

  return (
    <SideCard title="Organization">
      <div style={rows}>
        <div>
          {named ? (
            <Button as="a" variant="link-color" href={`/teams/${team.id}/crm/organizations/${organization.id}`}>
              {organization.name}
            </Button>
          ) : (
            <CellText primary="Archived organization" />
          )}
          {meta && <p style={muted}>{meta}</p>}
        </div>
        <CellText primary={orDash(contactName)} supporting="Primary contact" />
        <CellText primary={orDash(email)} supporting="Email" />
        <CellText primary={`${organization.grants_count || 0} · ${awardedTotal}`} supporting="Grants on record" />
      </div>
    </SideCard>
  );
}

OrganizationCard.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number }).isRequired,
  organization: PropTypes.object.isRequired,
  contact: PropTypes.object,
  contactName: PropTypes.string,
};

function FundingCard({ grant }) {
  const percent = fundingPercent(grant);
  return (
    <SideCard title="Funding">
      <div style={rows}>
        <CellText primary={money(grant.requested_amount)} supporting="Requested" />
        <CellText primary={money(grant.awarded_amount)} supporting="Awarded" />
        <CellText primary={formatCloseDate(grant.decision_on)} supporting="Decision" />
        {percent !== null && <ProgressBar value={percent} label="right" aria-label="Awarded of requested" />}
      </div>
    </SideCard>
  );
}

FundingCard.propTypes = { grant: PropTypes.object.isRequired };

function DocumentsCard({ documents, error, onShowDocuments }) {
  const documentMeta = (doc) => [DOCUMENT_CATEGORY_LABELS[doc.category], doc.human_created_at]
    .filter(Boolean)
    .join(' · ');

  let body = (
    <div style={rows}>
      {documents.slice(0, PREVIEW_DOCUMENTS).map((doc) => (
        <CellText key={doc.id} primary={doc.file_name} supporting={documentMeta(doc)} />
      ))}
    </div>
  );
  if (error) body = <p style={muted}>{"Couldn't load documents."}</p>;
  else if (documents.length === 0) body = <p style={muted}>No documents yet</p>;

  return (
    <SideCard
      title="Documents"
      actions={<Button variant="link-color" onClick={onShowDocuments}>View all</Button>}
    >
      {body}
    </SideCard>
  );
}

DocumentsCard.propTypes = {
  documents: PropTypes.arrayOf(PropTypes.object).isRequired,
  error: PropTypes.bool,
  onShowDocuments: PropTypes.func,
};

function GrantOverviewTab({
  team,
  csrfToken,
  grant,
  owners = [],
  canManage = false,
  onShowDocuments,
  documentsCount = 0,
  onDocumentsChange,
  onError,
}) {
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [notesError, setNotesError] = useState('');
  const [editing, setEditing] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsError, setDocumentsError] = useState(false);
  const loadedDocumentsCount = useRef(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const notesUrl = `/teams/${team.id}/crm/notes.json`;
  const noteUrl = (note) => `/teams/${team.id}/crm/notes/${note.id}.json`;
  const attachmentsUrl = `/teams/${team.id}/crm/grants/${grant.id}/attachments.json`;
  const requestConfig = { headers: { 'X-CSRF-TOKEN': csrfToken } };

  const loadNotes = useCallback(async () => {
    const response = await axios.get(notesUrl, {
      params: { notable_type: 'Deal', notable_id: grant.id },
      headers: { 'X-CSRF-TOKEN': csrfToken },
    });
    if (!mounted.current) return;
    setNotes(response.data || []);
    setNotesError('');
  }, [notesUrl, grant.id, csrfToken]);

  useEffect(() => {
    setNotesLoading(true);
    loadNotes()
      .catch(() => mounted.current && setNotesError('Failed to load notes'))
      .finally(() => mounted.current && setNotesLoading(false));
  }, [loadNotes]);

  const loadDocuments = useCallback(async () => {
    try {
      const response = await axios.get(attachmentsUrl, { headers: { 'X-CSRF-TOKEN': csrfToken } });
      if (!mounted.current) return;
      const list = response.data || [];
      loadedDocumentsCount.current = list.length;
      setDocuments(list);
      setDocumentsError(false);
      if (onDocumentsChange) onDocumentsChange(list.length);
    } catch (err) {
      if (mounted.current) setDocumentsError(true);
    }
  }, [attachmentsUrl, csrfToken, onDocumentsChange]);

  useEffect(() => {
    if (documentsCount === loadedDocumentsCount.current) return;
    loadDocuments();
  }, [documentsCount, loadDocuments]);

  const refreshNotesAfterChange = () => loadNotes().catch(() => onError(REFRESH_ERROR));

  const createNote = async (body) => {
    await axios.post(notesUrl, {
      note: {
        title: deriveNoteTitle(body),
        body,
        notable_type: 'Deal',
        notable_id: grant.id,
      },
    }, requestConfig);
    await refreshNotesAfterChange();
  };

  const startEdit = (note) => {
    setEditBody(plainBody(note));
    setEditing(note);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
  };

  const saveEdit = async () => {
    const body = editBody.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      await axios.patch(noteUrl(editing), { note: { title: deriveNoteTitle(body), body } }, requestConfig);
      setEditing(null);
      await refreshNotesAfterChange();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const closeDelete = () => {
    if (saving) return;
    setDeleting(null);
  };

  const confirmDelete = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.delete(noteUrl(deleting), requestConfig);
      setDeleting(null);
      await refreshNotesAfterChange();
    } catch (err) {
      setDeleting(null);
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={columns}>
      <div style={mainColumn}>
        <DetailsCard grant={grant} />

        {!isBlank(grant.description) && (
          <SideCard title="Description">
            <p style={description}>{grant.description}</p>
          </SideCard>
        )}

        <DealNotesTimeline
          notes={notes}
          loading={notesLoading}
          error={notesError}
          onCreateNote={createNote}
          onEditNote={startEdit}
          onDeleteNote={setDeleting}
          canManageNotes={canManage}
        />
      </div>

      <div style={sideColumn}>
        {grant.next_deadline && <NextDeadlineCard grant={grant} owners={owners} />}
        {grant.organization && (
          <OrganizationCard
            team={team}
            organization={grant.organization}
            contact={grant.contact}
            contactName={contactLabel(grant)}
          />
        )}
        <FundingCard grant={grant} />
        <DocumentsCard documents={documents} error={documentsError} onShowDocuments={onShowDocuments} />
      </div>

      <Modal
        open={Boolean(editing)}
        onClose={closeEdit}
        title="Edit note"
        footer={(
          <>
            <Button variant="secondary" onClick={closeEdit} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={saveEdit} disabled={saving || !editBody.trim()}>Save</Button>
          </>
        )}
      >
        <TextInput
          multiline
          rows={4}
          label="Note"
          value={editBody}
          disabled={saving}
          onChange={(event) => setEditBody(event.target.value)}
        />
      </Modal>

      <Modal
        open={Boolean(deleting)}
        onClose={closeDelete}
        type="error"
        title="Delete note?"
        description="This note will be permanently removed."
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />
    </div>
  );
}

GrantOverviewTab.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number }).isRequired,
  csrfToken: PropTypes.string,
  grant: PropTypes.object.isRequired,
  owners: PropTypes.array,
  canManage: PropTypes.bool,
  onShowDocuments: PropTypes.func,
  documentsCount: PropTypes.number,
  onDocumentsChange: PropTypes.func,
  onError: PropTypes.func.isRequired,
};

export default GrantOverviewTab;
